import { useLocation, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  Alert,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  ViewList as ViewListIcon,
  Person as PersonDetailIcon,
} from '@mui/icons-material'
import { Profile } from '@/types/profile'
import { ProfileListCard } from '@/components/ProfileListCard'
import { ProfileDetailView } from '@/components/ProfileDetailView'
import { useMyProfile } from '@/contexts/ProfileContext'

interface ConfirmLocationState {
  profile: Profile
  mode: 'register' | 'edit'
  pendingPhoto: { fileBase64: string; fileName: string } | null
}

const SECTION_LABEL_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  mb: 1.5,
}

export const ProfileConfirmPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { saveProfile } = useMyProfile()
  const [isConfirming, setIsConfirming] = useState(false)

  const state = location.state as ConfirmLocationState | null

  if (!state?.profile) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="error">プロフィールデータがありません。フォームから再度入力してください。</Alert>
      </Box>
    )
  }

  const { profile, mode, pendingPhoto } = state

  const getProfileColor = () => {
    if (profile.type === 'dating') return '#ec4899'
    if (profile.type === 'job') return '#059669'
    return theme.palette.primary.main
  }

  const handleConfirm = async () => {
    setIsConfirming(true)
    try {
      await saveProfile(profile, undefined, mode, undefined, pendingPhoto)
      if (mode === 'register') {
        navigate('/')
      } else {
        navigate(-2)
      }
    } finally {
      setIsConfirming(false)
    }
  }

  const handleBack = () => {
    navigate(-1)
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* ヘッダー */}
      <AppBar position="sticky" sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: getProfileColor() }}>
        <Toolbar sx={{ px: { xs: 1.5, sm: 2 } }}>
          <IconButton edge="start" color="inherit" onClick={handleBack} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            プロフィール確認
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        {/* 説明 */}
        <Alert severity="info" sx={{ mb: 3 }}>
          相手にはこのように表示されます。内容を確認してから確定してください。
        </Alert>

        {/* ── プレビュー 1: 一覧カード ── */}
        <Box sx={{ mb: 4 }}>
          <Box sx={SECTION_LABEL_SX}>
            <ViewListIcon sx={{ color: getProfileColor(), fontSize: 22 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              一覧表示（ルーム画面）
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            参加者一覧に並ぶカードのプレビューです。
          </Typography>
          <ProfileListCard profile={profile} isMobile={isMobile} preview />
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* ── プレビュー 2: 詳細カード ── */}
        <Box sx={{ mb: 4 }}>
          <Box sx={SECTION_LABEL_SX}>
            <PersonDetailIcon sx={{ color: getProfileColor(), fontSize: 22 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              詳細表示（プロフィール詳細画面）
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            「プロフィール」ボタンを押したときに相手に見える詳細画面のプレビューです。
          </Typography>
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <ProfileDetailView profile={profile} />
            </CardContent>
          </Card>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* ── アクションボタン ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={isConfirming ? undefined : <CheckCircleIcon />}
            onClick={handleConfirm}
            disabled={isConfirming}
            sx={{ bgcolor: getProfileColor(), '&:hover': { bgcolor: getProfileColor(), filter: 'brightness(0.9)' } }}
          >
            {isConfirming ? '保存中...' : mode === 'register' ? 'この内容で登録してルームへ' : 'この内容で更新する'}
          </Button>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            戻って修正する
          </Button>
        </Box>
      </Container>
    </Box>
  )
}
